/**
 * Trip CO2 Calculator — info tooltip.
 *
 * A small circular `?` icon that reveals an explainer popup on hover
 * or keyboard focus. JS-driven (not CSS `:hover`) so the show/hide
 * logic survives inline styling and works the same for mouse +
 * keyboard.
 *
 * The popup is centred above the trigger by default, but its
 * horizontal offset is clamped on every open so it never overflows
 * the viewport — measured via `getBoundingClientRect` on the trigger,
 * compared against `window.innerWidth`, and shifted by the deficit.
 *
 * Used by the chart legend (RFI swatch) and the perspective stat
 * (global per-capita reference value).
 */

import React, { useLayoutEffect, useRef, useState } from 'react';

export interface InfoTipProps {
  /** Tooltip contents (text or JSX). */
  content: React.ReactNode;
  /** Vertical anchor. Default 'top' (popup above the icon). */
  position?: 'top' | 'bottom';
  /** Accessible label for the trigger (default: "More information"). */
  ariaLabel?: string;
}

const POPUP_WIDTH = 280;
const VIEWPORT_MARGIN = 8;

const InfoTip: React.FC<InfoTipProps> = ({
  content,
  position = 'top',
  ariaLabel = 'More information',
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /**
   * Horizontal pixels to shift the popup from its centred position so
   * it never overflows the viewport. 0 when the centred position fits.
   */
  const [shiftX, setShiftX] = useState(0);

  // Measure and clamp on every open. useLayoutEffect (not useEffect)
  // so the shift is computed before the browser paints — avoids a
  // visible jump from centred → clamped.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const triggerCenterX = rect.left + rect.width / 2;
    const desiredLeft = triggerCenterX - POPUP_WIDTH / 2;
    const desiredRight = triggerCenterX + POPUP_WIDTH / 2;

    let shift = 0;
    if (desiredRight > window.innerWidth - VIEWPORT_MARGIN) {
      shift = window.innerWidth - VIEWPORT_MARGIN - desiredRight;
    } else if (desiredLeft < VIEWPORT_MARGIN) {
      shift = VIEWPORT_MARGIN - desiredLeft;
    }
    setShiftX(shift);
  }, [open]);

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        // Intentionally no `onClick` toggle: hover + focus are the
        // open paths. A toggle on top would let "click while hovering"
        // close the popup, which then can't reopen until the cursor
        // leaves and re-enters — confusing.
        style={{
          width: 16,
          height: 16,
          padding: 0,
          borderRadius: '50%',
          background: '#e2e8f0',
          color: '#475569',
          fontSize: 10,
          fontWeight: 800,
          border: 0,
          cursor: 'help',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        ?
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            ...(position === 'top'
              ? { bottom: 'calc(100% + 8px)' }
              : { top: 'calc(100% + 8px)' }),
            left: '50%',
            transform: `translateX(calc(-50% + ${shiftX}px))`,
            width: POPUP_WIDTH,
            maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
            background: '#0f172a',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1.45,
            padding: '8px 10px',
            borderRadius: 6,
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.25)',
            zIndex: 50,
            textAlign: 'left',
            fontWeight: 500,
            pointerEvents: 'none',
            // Reset inherited italic/letter-spacing from contexts like
            // the perspective stat's italic caption.
            fontStyle: 'normal',
            letterSpacing: 0,
          }}
        >
          {content}
        </span>
      )}
    </span>
  );
};

export default InfoTip;
